import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTranslation } from "react-i18next";
import { usePlaces } from "../api/hooks/usePlaces";
import useScrollToTop from "../hooks/useScrollToTop";

// Custom CSS for better map styling
const mapStyles = `
  .custom-popup .leaflet-popup-content-wrapper {
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }
  .custom-popup .leaflet-popup-tip {
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }
  .custom-popup .leaflet-popup-close-button {
    color: #6b7280;
    font-size: 18px;
    padding: 8px;
  }
  .custom-popup .leaflet-popup-close-button:hover {
    color: #374151;
  }
`;

const MapPage = () => {
  const { t, i18n } = useTranslation();
  useScrollToTop(); // Scroll to top on navigation
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const { data: places, isLoading, error } = usePlaces();

  useEffect(() => {
    // Inject custom CSS for map styling
    const styleElement = document.createElement("style");
    styleElement.textContent = mapStyles;
    document.head.appendChild(styleElement);

    if (!mapRef.current) return;

    const map = L.map(mapRef.current, {
      center: [20, 0],
      zoom: 2,
      scrollWheelZoom: true,
    });
    leafletMapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    const rows = Array.isArray(places) ? places : [];

    const shapes = rows.length
      ? rows.flatMap((row) => expandGeometries(row))
      : [];

    renderShapes(map, shapes, i18n.language, t);

    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
      // Clean up injected CSS
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, [places]);

  if (isLoading) {
    return (
      <section className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            {t("common.loading", { defaultValue: "Loading..." })}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <p className="text-red-600">
              {t("map.errorLoading", {
                defaultValue: "Error loading places data",
              })}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-bold text-foreground mb-1">
            {t("map.title", { defaultValue: "Travel Map" })}
          </h1>
          <p className="text-muted-foreground">
            {t("map.subtitle", {
              defaultValue: "Explore places we've visited around the world",
            })}
          </p>
        </div>
        <div className="rounded-lg overflow-hidden border border-border">
          <div ref={mapRef} style={{ height: "70vh", width: "100%" }} />
        </div>
      </div>
    </section>
  );
};

function expandGeometries(row) {
  const { id, place, articles } = row || {};
  if (!place) return [];

  if (Array.isArray(place.coords)) {
    return [
      {
        kind: "point",
        id,
        coords: place.coords,
        name: place.name,
        note: place.note,
        articles,
      },
    ];
  }

  if (place.type === "Point" && Array.isArray(place.coordinates)) {
    const [lng, lat] = place.coordinates;
    return [{ kind: "point", id, coords: [lat, lng], articles }];
  }

  if (place.type === "LineString" && Array.isArray(place.coordinates)) {
    const path = place.coordinates
      .filter((c) => Array.isArray(c) && c.length === 2)
      .map(([lng, lat]) => [lat, lng]);
    return path.length ? [{ kind: "line", id, path }] : [];
  }

  if (place.type === "GeometryCollection" && Array.isArray(place.geometries)) {
    return place.geometries.flatMap((g, idx) => {
      if (g.type === "Point" && Array.isArray(g.coordinates)) {
        const [lng, lat] = g.coordinates;
        return [
          { kind: "point", id: `${id}-p${idx}`, coords: [lat, lng], articles },
        ];
      }
      if (g.type === "LineString" && Array.isArray(g.coordinates)) {
        const path = g.coordinates
          .filter((c) => Array.isArray(c) && c.length === 2)
          .map(([lng, lat]) => [lat, lng]);
        return path.length ? [{ kind: "line", id: `${id}-l${idx}`, path }] : [];
      }
      return [];
    });
  }

  return [];
}

function renderShapes(map, shapes, currentLanguage = "en-US", t) {
  shapes.forEach((s) => {
    if (s.kind === "point") {
      const title = s.name || s.title || "";
      const note = s.note || s.description || "";

      // Build popup content with article information
      let popupContent = `<div style="min-width:200px">`;

      if (title) {
        popupContent += `<div style="font-weight:600;margin-bottom:8px;font-size:14px">${title}</div>`;
      }

      if (note) {
        popupContent += `<div style="font-size:12px;color:#555;margin-bottom:8px">${note}</div>`;
      }

      // Add article information if available
      if (s.articles && Array.isArray(s.articles) && s.articles.length > 0) {
        popupContent += `<div style="padding-top:8px;margin-top:8px">`;
        popupContent += `<div style="font-weight:600;margin-bottom:4px;color:#219fa0">${t(
          "map.relatedArticles",
          { defaultValue: "Related Articles" }
        )}:</div>`;

        s.articles.forEach((articleRef, idx) => {
          const article = articleRef.Articles_id;
          if (
            article &&
            article.translations &&
            Array.isArray(article.translations)
          ) {
            // Try to find translation in current language, fallback to first available
            const currentLang = currentLanguage || "en-US";
            const langPrefix = currentLang.split("-")[0];

            const exactMatch = article.translations.find(
              (t) => t.languages_code === currentLang
            );
            const prefixMatch = article.translations.find((t) =>
              t.languages_code?.startsWith(langPrefix)
            );
            const fallbackMatch = article.translations.find(
              (t) => t.languages_code === "en-US"
            );

            const translation =
              exactMatch ||
              prefixMatch ||
              fallbackMatch ||
              article.translations[0];
            const articleTitle = translation.title || "Untitled";
            const publishedDate = article.published_at
              ? new Date(article.published_at).toLocaleDateString(currentLang)
              : "";

            popupContent += `<div style="margin-bottom:6px">`;
            // Make article title clickable with hover effect - use the correct article ID
            popupContent += `<div style="font-weight:500;font-size:13px;color:#219fa0;cursor:pointer;text-decoration:underline;hover:color:#1a7a7a" onclick="window.open('/blog/${
              articleRef.Articles_id.id || articleRef.Articles_id
            }', '_blank')">${articleTitle}</div>`;
            if (publishedDate) {
              popupContent += `<div style="font-size:11px;color:#888">${publishedDate}</div>`;
            }
            popupContent += `</div>`;
          }
        });

        popupContent += `</div>`;
      } else {
        // Show message when no articles are available
        popupContent += `<div style="padding-top:8px;margin-top:8px">`;
        popupContent += `<div style="font-size:12px;color:#888;font-style:italic">${t(
          "map.noArticles",
          { defaultValue: "No articles available for this location" }
        )}</div>`;
        popupContent += `</div>`;
      }

      popupContent += `</div>`;

      // Create a more attractive pin with better colors
      const pin = L.circleMarker(s.coords, {
        radius: 8,
        color: "#1e40af", // Darker blue border
        weight: 2,
        fillColor: "#3b82f6", // Bright blue fill
        fillOpacity: 0.9,
        className: "custom-pin",
      }).addTo(map);

      // Add a subtle shadow effect
      pin.setStyle({
        shadow: true,
        shadowColor: "#1e3a8a",
        shadowOpacity: 0.3,
        shadowRadius: 4,
      });

      // Bind popup with better styling
      pin.bindPopup(popupContent, {
        className: "custom-popup",
        maxWidth: 300,
        closeButton: true,
        autoClose: false,
        closeOnClick: false,
      });
    }
    if (s.kind === "line") {
      L.polyline(s.path, {
        color: "#1e40af",
        weight: 2,
        opacity: 0.9,
        className: "custom-line",
      }).addTo(map);
    }
  });
}

export default MapPage;
